import React, { useState, useEffect, useRef } from 'react';
import { BrandLogo } from '../components/BrandLogo';
import { Link } from 'react-router-dom';
import { 
  Video, Play, ArrowRight, Check, Users, Globe, 
  ChevronDown, ChevronUp, FileText, Mail, Phone,
  Target, Briefcase, Share2, Zap, Calendar,
  MessageSquareQuote, BarChart3, Images, Mic, Award, TrendingUp,
  Handshake, Building2, Star, ChevronLeft, ChevronRight, ExternalLink,
  Languages, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// HERO SECTION - Video Background + 2 Boxes
// ============================================
function HeroSection() {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(true);

  const toggleVideo = () => {
    if (!videoRef.current) return;
    if (videoPlaying) { videoRef.current.pause(); } else { videoRef.current.play(); }
    setVideoPlaying(!videoPlaying);
  };

  return (
    <section className="relative min-h-[92vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Video Background — full hero, unblurred */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay muted loop playsInline
        poster="/images/hero-leadership.png"
        data-testid="hero-background-video"
      >
        <source src="/videos/hero-bg.mp4" type="video/mp4" />
      </video>

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-zinc-950/40" />

      {/* Subtle accent glow */}
      <div className="absolute bottom-0 left-0 w-[500px] h-[350px] bg-[#C41E3A]/10 blur-[140px] rounded-full pointer-events-none" />

      {/* Video play/pause */}
      <button
        onClick={toggleVideo}
        className="absolute top-24 right-6 z-20 flex items-center gap-1.5 bg-zinc-900/50 backdrop-blur-sm border border-zinc-700/40 text-zinc-300 hover:text-white px-2.5 py-1 rounded-full text-xs transition-all hover:bg-zinc-800/70"
        data-testid="hero-video-toggle"
      >
        {videoPlaying ? (
          <><span className="w-1.5 h-1.5 rounded-full bg-[#C41E3A] animate-pulse" /><span>Pause</span></>
        ) : (
          <><Play className="w-3 h-3" /><span>Play</span></>
        )}
      </button>

      {/* Content Grid */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 w-full">
        <div className="lg:grid lg:grid-cols-12 lg:gap-10 lg:items-center">

          {/* Left Column — Primary Message (7 cols) */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex flex-wrap items-center gap-3" data-testid="hero-platform-badge">
              <span className="inline-flex items-center gap-2 bg-[#C41E3A]/10 border border-[#C41E3A]/20 text-[#C41E3A] text-xs font-semibold tracking-wider uppercase px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C41E3A] animate-pulse" />
                Video-First Platform for B2B Leaders
              </span>
              <span className="inline-flex items-center gap-1.5 text-zinc-500 text-xs" data-testid="hero-privacy-note">
                <Shield className="w-3.5 h-3.5 text-zinc-500" />
                Private by design
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-black tracking-tight leading-[1.1]" data-testid="hero-main-heading">
              New markets. New partners.
              <span className="block text-zinc-400 mt-1">One video-powered introduction.</span>
            </h1>

            <p className="text-lg sm:text-xl font-bold text-white border-l-2 border-[#C41E3A] pl-4" data-testid="hero-tagline">
              One profile. One link. One unforgettable first impression.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                to="/profile/create"
                className="group bg-[#C41E3A] hover:bg-[#A01830] text-white px-7 py-3.5 rounded-lg text-center font-bold transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-[#C41E3A]/25 hover:shadow-[#C41E3A]/40"
                data-testid="hero-primary-cta"
              >
                Create Your Profile
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#contact"
                className="border border-zinc-700/60 bg-zinc-900/40 backdrop-blur-sm hover:bg-zinc-800/60 text-zinc-300 hover:text-white px-7 py-3.5 rounded-lg text-center font-medium transition-all flex items-center justify-center gap-2"
                data-testid="hero-secondary-cta"
              >
                <Video className="w-4 h-4" />
                Book a professional interview
              </a>
            </div>

            <a href="#video-first" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-[#C41E3A] transition-colors" data-testid="hero-why-videofirst-link">
              <Play className="w-3.5 h-3.5" />
              Why video-first? AI is changing how leaders are discovered →
            </a>
          </div>

          {/* Right Column — 2 Boxes only (5 cols) */}
          <div className="lg:col-span-5 mt-10 lg:mt-0 space-y-4">
            {/* Box 1: Problem & Solution */}
            <div className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/70 rounded-2xl p-5 md:p-6 space-y-4" data-testid="hero-problem-solution-card">
              <p className="text-zinc-200 text-sm leading-relaxed">
                Before every B2B meeting, they research you. Before every deal, they Google your name.
              </p>
              <p className="text-white font-semibold text-base leading-relaxed">
                30 seconds of video to stand out — without being in the room.
              </p>
              <div className="h-px bg-zinc-700/50" />
              <p className="text-zinc-300 text-sm leading-relaxed">
                BrandStage ensures they find a leader worth meeting.
              </p>
            </div>

            {/* Box 2: Language Translation */}
            <div className="bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/60 rounded-xl px-4 py-3" data-testid="hero-language-badge">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-[#C41E3A]" />
                <span className="text-zinc-400 text-xs">Profile text auto-translated to</span>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {['🇬🇧', '🇻🇳', '🇹🇭', '🇨🇳', '🇯🇵', '🇮🇳', '🇦🇪', '🇮🇩', '🇲🇾', '🇰🇷', '🇩🇪', '🇫🇷', '🇪🇸'].map((flag, i) => (
                  <span key={i} className="text-base hover:scale-125 transition-transform cursor-default">{flag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-zinc-950 to-transparent" />
    </section>
  );
}

// ============================================
// PROFILE FEATURES SECTION - What's Inside
// ============================================
function ProfileFeaturesSection() {
  const features = [
    {
      icon: <Video className="w-7 h-7" />,
      title: "Leadership Video",
      description: "Your story, professionally captured. A 2-3 minute interview that introduces you before any meeting.",
      highlight: "First impression"
    },
    {
      icon: <MessageSquareQuote className="w-7 h-7" />,
      title: "Video Testimonials",
      description: "Clients and partners vouch for you on camera. Collect testimonials with one shareable link.",
      highlight: "Social proof"
    },
    {
      icon: <Languages className="w-7 h-7" />,
      title: "13 Languages",
      description: "Profile text auto-translates to Vietnamese, Thai, Chinese, Japanese, Hindi, Arabic, and more. Reach global audiences instantly.",
      highlight: "Global reach"
    },
    {
      icon: <Images className="w-7 h-7" />,
      title: "Event Showcase",
      description: "Photo galleries and video highlights from trade shows, conferences, and key moments.",
      highlight: "Your presence"
    },
    {
      icon: <BarChart3 className="w-7 h-7" />,
      title: "Digital Readiness Score",
      description: "See how your online presence stacks up. Get actionable insights to improve visibility.",
      highlight: "Benchmarked"
    }
  ];

  return (
    <section className="py-20 bg-zinc-950" data-testid="features-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-14">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">What's Inside Your Profile</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">
            Everything a decision-maker needs to say yes
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            More than a digital business card. A living portfolio that builds trust before you even meet.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group bg-zinc-900/50 border border-zinc-800 hover:border-[#C41E3A]/50 rounded-2xl p-6 transition-all duration-300 hover:bg-zinc-900"
            >
              <div className="w-14 h-14 bg-[#C41E3A]/10 rounded-xl flex items-center justify-center text-[#C41E3A] mb-5 group-hover:bg-[#C41E3A]/20 transition-colors">
                {feature.icon}
              </div>
              <span className="text-[#C41E3A] text-xs font-bold uppercase tracking-wider">{feature.highlight}</span>
              <h3 className="text-xl font-bold mt-1 mb-3">{feature.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
        
        {/* CTA */}
        <div className="text-center mt-12">
          <Link 
            to="/p/brandstage" 
            className="inline-flex items-center gap-2 text-[#C41E3A] hover:text-white font-semibold transition-colors"
          >
            See a live profile example
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================
// VIDEO-FIRST PHILOSOPHY SECTION
// ============================================
function VideoFirstSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  
  // Featured video - Leadership interview sample
  const featuredVideoId = "23M63uS7DOM";
  
  const highlights = [
    { value: "150+", label: "trade missions organized" },
    { value: "2007", label: "Continex founded" },
    { value: "48hr", label: "typical turnaround*" }
  ];

  return (
    <section id="video-first" className="py-20 bg-gradient-to-b from-zinc-900 to-zinc-950 overflow-hidden" data-testid="video-first-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div>
            <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">Video-First Philosophy</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-6">
              In a world of text,<br />
              <span className="text-zinc-400">leaders show up on video.</span>
            </h2>
            
            <p className="text-lg text-zinc-300 leading-relaxed mb-4">
              Trade shows, investor pitches, partnership meetings—the executives who win are the ones 
              people remember. Video captures your presence, your conviction, your story in a way 
              no PDF ever will.
            </p>
            
            {/* AI/LLM insight */}
            <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 mb-6">
              <p className="text-sm text-zinc-300 leading-relaxed">
                <span className="text-white font-semibold">The AI advantage:</span> LLMs and search engines now analyze video content—transcribing, indexing, and surfacing leaders who show up on camera. 
                <span className="text-[#C41E3A]"> Text profiles get skimmed. Video profiles get remembered.</span>
              </p>
            </div>
            
            <div className="space-y-4 mb-8">
              {[
                { icon: <Mic className="w-5 h-5" />, text: "Professional interviews at trade shows & events" },
                { icon: <Award className="w-5 h-5" />, text: "Branded videos with your identity" },
                { icon: <TrendingUp className="w-5 h-5" />, text: "AI-discoverable content across platforms" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-zinc-300">
                  <div className="w-10 h-10 bg-[#C41E3A]/10 rounded-lg flex items-center justify-center text-[#C41E3A]">
                    {item.icon}
                  </div>
                  {item.text}
                </div>
              ))}
            </div>
            
            {/* Mini stats */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-zinc-800">
              {highlights.map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-2xl font-black text-white">{stat.value}</p>
                  <p className="text-xs text-zinc-500">{stat.label}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-600 mt-2">*Standard projects. Complex edits may require additional time.</p>
          </div>
          
          {/* Right - Video Player with Trade Show imagery */}
          <div className="relative">
            {/* Background decorative images - trade show atmosphere */}
            <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#C41E3A]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl" />
            
            {/* Main video container */}
            <div className="relative rounded-2xl overflow-hidden border-2 border-zinc-800 shadow-2xl shadow-black/50">
              {isPlaying ? (
                <div className="aspect-video">
                  <iframe
                    ref={videoRef}
                    src={`https://www.youtube.com/embed/${featuredVideoId}?rel=0&autoplay=1`}
                    title="Leadership Interview Sample"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              ) : (
                <div 
                  className="aspect-video relative cursor-pointer group"
                  onClick={() => setIsPlaying(true)}
                >
                  {/* Video thumbnail */}
                  <img 
                    src={`https://img.youtube.com/vi/${featuredVideoId}/maxresdefault.jpg`}
                    alt="Leadership Interview"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  
                  {/* Play button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 bg-[#C41E3A] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl shadow-[#C41E3A]/30">
                      <Play className="w-8 h-8 text-white fill-white ml-1" />
                    </div>
                  </div>
                  
                  {/* Caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-white font-bold text-lg">See a Leadership Interview</p>
                    <p className="text-zinc-400 text-sm">2 min • Trade Show Coverage</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Floating badge */}
            <div className="absolute -bottom-4 -right-4 bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-zinc-300">Continex: 150+ Trade Missions</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// OUTCOMES SECTION - What Leaders Achieve
// ============================================
function OutcomesSection() {
  const outcomes = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Instant Credibility",
      description: "Prospects see your expertise, testimonials, and vision in one place—before you even speak."
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "One Link, Everywhere",
      description: "Share on WhatsApp, LinkedIn, email signatures, business cards. One URL that represents you completely."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "A Profile That Grows",
      description: "Add videos, collect testimonials, update achievements. Your digital presence evolves with your business."
    }
  ];

  return (
    <section className="py-20 bg-zinc-900" data-testid="outcomes-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">What You Get</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">
            More than a profile. A leadership platform.
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {outcomes.map((outcome, index) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-[#C41E3A]/10 rounded-2xl flex items-center justify-center text-[#C41E3A] mx-auto mb-5">
                {outcome.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{outcome.title}</h3>
              <p className="text-zinc-400 leading-relaxed">{outcome.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// SOCIAL PROOF - Stats & Credibility
// ============================================
function SocialProofSection() {
  const stats = [
    { value: "2007", label: "Continex Founded" },
    { value: "150+", label: "Trade Missions" },
    { value: "12", label: "Countries" },
    { value: "48hr", label: "Typical Turnaround*" }
  ];

  return (
    <section className="py-16 bg-zinc-950 border-y border-zinc-800" data-testid="social-proof-section">
      <div className="max-w-5xl mx-auto px-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl md:text-4xl font-black text-white mb-1">{stat.value}</p>
              <p className="text-zinc-500 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
        
        <p className="text-center text-zinc-600 text-xs mb-8">*Standard projects. Complex edits may vary.</p>
        
        {/* Credibility line */}
        <p className="text-center text-zinc-400">
          <span className="text-white font-medium">BrandStage</span> (launched 2025) is a brand of Continex Tradeline India Private Limited, 
          operating across international markets since 2007.
        </p>
      </div>
    </section>
  );
}

// ============================================
// LIVE PROFILES CAROUSEL - Real Examples
// ============================================
function LiveProfilesSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Featured profile slugs to showcase (real profiles from production)
  const featuredSlugs = ['brandstage', 'elobau-gmbh', 'cinimage'];
  
  // Fallback profiles if API fails
  const fallbackProfiles = [
    {
      slug: 'brandstage',
      company_name: 'BrandStage',
      tagline: 'Video-first profiles for business leaders',
      logo_url: null,
      category: 'Media Production',
      kpis: { years_in_business: '18+', countries: '12', projects: '150+' }
    },
    {
      slug: 'elobau-gmbh',
      company_name: 'elobau GmbH',
      tagline: 'Sensor Technology & Human-Machine Interface',
      logo_url: null,
      category: 'Manufacturing',
      kpis: { years_in_business: '50+', countries: '8', projects: '1000+' }
    },
    {
      slug: 'cinimage',
      company_name: 'CINIMAGE',
      tagline: 'Visual Storytelling for Brands',
      logo_url: null,
      category: 'Creative Agency',
      kpis: { years_in_business: '15+', countries: '5', projects: '300+' }
    }
  ];

  useEffect(() => {
    // Try to fetch real profiles
    const fetchProfiles = async () => {
      try {
        const responses = await Promise.all(
          featuredSlugs.map(slug => 
            fetch(`${API_URL}/api/p/${slug}`).then(r => r.ok ? r.json() : null)
          )
        );
        const validProfiles = responses.filter(p => p !== null);
        setProfiles(validProfiles.length > 0 ? validProfiles : fallbackProfiles);
      } catch (err) {
        setProfiles(fallbackProfiles);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, []);

  // Auto-rotate carousel
  useEffect(() => {
    if (profiles.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % profiles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [profiles.length]);

  const nextProfile = () => setCurrentIndex(prev => (prev + 1) % profiles.length);
  const prevProfile = () => setCurrentIndex(prev => (prev - 1 + profiles.length) % profiles.length);

  if (loading) return null;

  return (
    <section className="py-20 bg-zinc-900" data-testid="live-profiles-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">Live Profiles</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">
            See BrandStage in action
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Real companies using BrandStage to tell their story and connect with global opportunities.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {/* Profile Card */}
          <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden shadow-2xl">
            {profiles.map((profile, index) => (
              <div 
                key={profile.slug}
                className={`transition-all duration-500 ${index === currentIndex ? 'block' : 'hidden'}`}
              >
                <div className="grid md:grid-cols-2">
                  {/* Left - Profile Preview */}
                  <div className="p-8 md:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      {profile.logo_url ? (
                        <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center p-2 flex-shrink-0">
                          <img 
                            src={profile.logo_url} 
                            alt={profile.company_name} 
                            className="max-w-full max-h-full object-contain" 
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 bg-gradient-to-br from-[#C41E3A] to-[#8B1429] rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                          {profile.company_name?.charAt(0) || 'C'}
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-bold text-white">{profile.company_name}</h3>
                        {profile.category && (
                          <span className="text-xs bg-[#C41E3A]/20 text-[#C41E3A] px-2 py-1 rounded-full">
                            {profile.category}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-zinc-300 mb-6 leading-relaxed">
                      {profile.tagline || profile.about?.substring(0, 150) + '...' || 'Discover how this company presents their story with video-first profiles.'}
                    </p>
                    
                    {/* KPIs */}
                    {profile.kpis && (
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {profile.kpis.years_in_business && (
                          <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                            <p className="text-xl font-bold text-white">{profile.kpis.years_in_business}</p>
                            <p className="text-xs text-zinc-500">Years</p>
                          </div>
                        )}
                        {profile.kpis.countries && (
                          <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                            <p className="text-xl font-bold text-white">{profile.kpis.countries}</p>
                            <p className="text-xs text-zinc-500">Countries</p>
                          </div>
                        )}
                        {(profile.kpis.projects || profile.kpis.clients) && (
                          <div className="text-center p-3 bg-zinc-800/50 rounded-lg">
                            <p className="text-xl font-bold text-white">{profile.kpis.projects || profile.kpis.clients}</p>
                            <p className="text-xs text-zinc-500">Projects</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <Link 
                      to={`/p/${profile.slug}`}
                      className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                    >
                      View Full Profile
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                  
                  {/* Right - Visual/Video Preview */}
                  <div className="relative bg-zinc-950 min-h-[300px] flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#C41E3A]/20 to-transparent" />
                    <div className="relative text-center p-8">
                      <div className="w-20 h-20 bg-[#C41E3A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Video className="w-10 h-10 text-[#C41E3A]" />
                      </div>
                      <p className="text-zinc-400 text-sm">Video-first profile</p>
                      <p className="text-white font-medium">with testimonials & showcase</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation */}
          {profiles.length > 1 && (
            <>
              <button 
                onClick={prevProfile}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 w-10 h-10 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={nextProfile}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 w-10 h-10 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {profiles.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? 'w-8 bg-[#C41E3A]' : 'bg-zinc-600 hover:bg-zinc-500'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
        
        {/* CTA */}
        <div className="text-center mt-10">
          <Link 
            to="/profile/create" 
            className="inline-flex items-center gap-2 text-[#C41E3A] hover:text-white font-semibold transition-colors"
          >
            Create your profile like these →
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================
// HOW IT WORKS - Simple 3 Steps
// ============================================
function HowItWorksSection() {
  const steps = [
    {
      number: "01",
      title: "Tell Your Story",
      description: "Answer a few questions about your business. Our AI crafts your professional narrative."
    },
    {
      number: "02",
      title: "Add Your Videos",
      description: "Embed YouTube videos or book a professional interview with our team."
    },
    {
      number: "03",
      title: "Share Everywhere",
      description: "One link for WhatsApp, LinkedIn, email, and QR codes. Update anytime."
    }
  ];

  return (
    <section className="py-20 bg-zinc-900" data-testid="how-it-works-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">How It Works</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">
            Live in minutes. Impressive for years.
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="text-6xl font-black text-[#C41E3A]/20 mb-4">{step.number}</div>
              <h3 className="text-xl font-bold mb-3">{step.title}</h3>
              <p className="text-zinc-400">{step.description}</p>
              
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-[#C41E3A]/30 to-transparent" style={{ width: '50%', left: '80%' }} />
              )}
            </div>
          ))}
        </div>
        
        <div className="text-center">
          <Link 
            to="/profile/create" 
            className="inline-flex items-center gap-2 bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-lg font-bold transition-colors"
          >
            Create Your Profile
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// ============================================
// VIDEOS SECTION - See It In Action
// ============================================
function VideosSection() {
  const [playingVideo, setPlayingVideo] = useState(null);
  
  const videos = [
    { id: "LpXrfQ-N41I", title: "BrandStage for Trade Shows" },
    { id: "23M63uS7DOM", title: "Leadership Interview Sample" },
    { id: "7fgQPgboYj4", title: "Trade Delegation Coverage" }
  ];

  return (
    <section className="py-20 bg-zinc-950" data-testid="videos-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">See It In Action</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">
            Leadership stories, professionally told
          </h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <div key={index} className="group">
              <div className="aspect-video bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 group-hover:border-[#C41E3A]/50 transition-colors">
                {playingVideo === video.id ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${video.id}?rel=0&autoplay=1`}
                    title={video.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ) : (
                  <div 
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => setPlayingVideo(video.id)}
                  >
                    <img 
                      src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
                      <div className="w-14 h-14 bg-[#C41E3A] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Play className="w-6 h-6 text-white fill-white ml-1" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-zinc-500 text-sm mt-3 text-center">{video.title}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// TRADE SHOWS SECTION - Separate Vertical
// ============================================
function TradeShowsSection() {
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="py-20 bg-zinc-900" data-testid="tradeshows-section">
      <div className="max-w-5xl mx-auto px-6">
        <div 
          className="bg-gradient-to-r from-[#C41E3A]/10 to-[#C41E3A]/5 border border-[#C41E3A]/30 rounded-2xl p-8 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-[#C41E3A]/20 rounded-xl flex items-center justify-center">
                <Briefcase className="w-7 h-7 text-[#C41E3A]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Attending a Trade Show?</h3>
                <p className="text-zinc-400">Maximize your ROI with professional video coverage</p>
              </div>
            </div>
            <button className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
              {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          
          {expanded && (
            <div className="mt-8 pt-8 border-t border-[#C41E3A]/20">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Professional Interviews */}
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Video className="w-5 h-5 text-[#C41E3A]" />
                    Professional Booth Interviews
                  </h4>
                  <p className="text-zinc-400 text-sm mb-4">
                    Our team films leadership interviews at your booth. Receive edited videos + social reels within 48 hours.
                  </p>
                  <ul className="space-y-2 text-sm text-zinc-300 mb-4">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />2-3 min branded interview</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />2 × 30-sec social reels</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />48-hour turnaround</li>
                  </ul>
                  <a href="#contact" className="text-[#C41E3A] font-semibold hover:underline text-sm">
                    Book your slot →
                  </a>
                </div>
                
                {/* Quick Bites */}
                <div>
                  <h4 className="text-lg font-bold mb-3 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#C41E3A]" />
                    Quick Bites Self-Recording
                  </h4>
                  <p className="text-zinc-400 text-sm mb-4">
                    Capture 30-40 second clips instantly at your booth. Visitor testimonials, product teasers, event buzz—delivered to email in seconds.
                  </p>
                  <ul className="space-y-2 text-sm text-zinc-300 mb-4">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />Self-service kiosk</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />Branded intro/outro</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#C41E3A]" />Instant email delivery</li>
                  </ul>
                  <Link to="/booth" className="text-[#C41E3A] font-semibold hover:underline text-sm">
                    Try Quick Bites →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================
// BRANDSTAGE CONNECT - Business Opportunities
// ============================================
function ConnectSection() {
  const categories = [
    { icon: <Briefcase className="w-6 h-6" />, label: 'Business Leads', color: 'text-blue-400 bg-blue-500/20', desc: 'Find and be found by potential clients' },
    { icon: <Handshake className="w-6 h-6" />, label: 'Partnerships', color: 'text-purple-400 bg-purple-500/20', desc: 'Strategic alliances and JVs' },
    { icon: <Calendar className="w-6 h-6" />, label: 'Events', color: 'text-pink-400 bg-pink-500/20', desc: 'Trade shows and conferences' },
    { icon: <Mail className="w-6 h-6" />, label: 'Invitations', color: 'text-green-400 bg-green-500/20', desc: 'Exclusive opportunities' },
  ];

  return (
    <section className="py-20 bg-zinc-950" data-testid="connect-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-400 px-3 py-1.5 rounded-full text-sm font-medium mb-4">
              <Star className="w-4 h-4" />
              BrandStage Connect
              <span className="bg-amber-500/20 text-amber-400 text-[10px] px-2 py-0.5 rounded-full font-bold ml-1">COMING SOON</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-6">
              Your profile is your pitch.<br />
              <span className="text-zinc-400">Now find the opportunity.</span>
            </h2>
            
            <p className="text-lg text-zinc-300 leading-relaxed mb-8">
              BrandStage Connect is where global business opportunities will meet verified profiles. 
              Post opportunities, respond to leads, and connect with decision-makers—all backed by 
              the credibility of your BrandStage profile.
            </p>
            
            <div className="grid grid-cols-2 gap-4 mb-8">
              {categories.map((cat, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
                  <div className={`p-2 rounded-lg ${cat.color}`}>
                    {cat.icon}
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{cat.label}</p>
                    <p className="text-zinc-500 text-xs">{cat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <span 
                className="inline-flex items-center justify-center gap-2 bg-purple-600/50 text-white/70 px-6 py-3 rounded-lg font-semibold cursor-not-allowed"
              >
                Explore Opportunities
                <span className="text-[10px] bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full font-bold">SOON</span>
              </span>
              <span 
                className="inline-flex items-center justify-center gap-2 border border-zinc-700/50 text-white/50 px-6 py-3 rounded-lg font-semibold cursor-not-allowed"
              >
                Post an Opportunity
                <span className="text-[10px] bg-amber-500/30 text-amber-300 px-2 py-0.5 rounded-full font-bold">SOON</span>
              </span>
            </div>
          </div>
          
          {/* Right - Visual Preview */}
          <div className="relative">
            {/* Background glow */}
            <div className="absolute inset-0 bg-purple-500/10 blur-3xl rounded-full" />
            
            {/* Example label */}
            <p className="text-center text-zinc-500 text-xs mb-3 italic">Example opportunities</p>
            
            {/* Opportunity Cards Stack */}
            <div className="relative space-y-4">
              {/* Sample Opportunity Card 1 */}
              <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 transform rotate-1 hover:rotate-0 transition-transform">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <span className="text-xs bg-zinc-700/50 text-zinc-400 px-2 py-1 rounded-full">Example</span>
                </div>
                <h4 className="font-bold text-white mb-1">Seeking Distribution Partners in APAC</h4>
                <p className="text-zinc-500 text-sm mb-3">Pharma company expanding to Southeast Asia...</p>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Globe className="w-3 h-3" />
                  Healthcare • Singapore, Thailand
                </div>
              </div>
              
              {/* Sample Opportunity Card 2 */}
              <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 transform -rotate-1 hover:rotate-0 transition-transform ml-8">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                    <Users className="w-4 h-4" />
                  </div>
                  <span className="text-xs bg-zinc-700/50 text-zinc-400 px-2 py-1 rounded-full">Example</span>
                </div>
                <h4 className="font-bold text-white mb-1">JV Partner for Tech Manufacturing</h4>
                <p className="text-zinc-500 text-sm mb-3">Looking for local manufacturing partner...</p>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Globe className="w-3 h-3" />
                  Technology • India, UAE
                </div>
              </div>
              
              {/* Sample Opportunity Card 3 */}
              <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 transform rotate-0.5 hover:rotate-0 transition-transform mr-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-lg bg-pink-500/20 text-pink-400">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <span className="text-xs bg-zinc-700/50 text-zinc-400 px-2 py-1 rounded-full">Example</span>
                </div>
                <h4 className="font-bold text-white mb-1">Thai Content Market 2026 - Booth Inquiries</h4>
                <p className="text-zinc-500 text-sm mb-3">Reserve your presence at Thailand's biggest...</p>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Globe className="w-3 h-3" />
                  Media & Entertainment • Bangkok
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// PRICING SECTION - Simple Two Options
// ============================================
function PricingSection() {
  return (
    <section id="pricing" className="py-20 bg-zinc-950" data-testid="pricing-section">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">Get Started</p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight">
            Choose your path
          </h2>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Profile */}
          <div className="bg-zinc-900 border-2 border-green-500/50 rounded-2xl p-8 relative">
            <div className="absolute -top-3 left-6 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              FROM ₹4,999
            </div>
            <h3 className="text-2xl font-bold mb-2">Company Profile</h3>
            <p className="text-zinc-400 mb-1">Self-service, live in minutes</p>
            <p className="text-sm text-zinc-500 mb-6">Build and preview free, pay only to publish. Add Pro features or go All-Access.</p>
            
            <ul className="space-y-3 mb-8">
              {[
                'AI-generated professional narrative',
                'YouTube video embedding',
                'Contact form & enquiries',
                'Share via WhatsApp, LinkedIn, Email',
                'QR code for print materials',
                'Unlimited updates'
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            
            <Link 
              to="/profile/create"
              className="block w-full bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-bold text-center transition-colors"
            >
              Create Your Profile
            </Link>
            <Link to="/pricing" className="block text-center text-sm text-zinc-400 hover:text-white mt-3" data-testid="landing-compare-plans">Compare Basic, Pro features & All-Access →</Link>
          </div>
          
          {/* Professional Interview */}
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-2">Professional Interview</h3>
            <p className="text-zinc-400 mb-2">Filmed by our team</p>
            <p className="text-sm text-zinc-500 mb-6">Custom quote based on location & scope</p>
            
            <ul className="space-y-3 mb-8">
              {[
                'Everything in the Company Profile',
                'Professional video interview',
                'On-location or trade show filming',
                'Edited videos + social reels',
                '48-hour typical turnaround*',
                '"Produced by BrandStage" badge'
              ].map((feature, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-zinc-300">
                  <Check className="w-4 h-4 text-[#C41E3A] flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            
            <a 
              href="#contact"
              className="block w-full bg-[#C41E3A] hover:bg-[#A01830] text-white py-3 rounded-lg font-bold text-center transition-colors"
            >
              Request a Quote
            </a>
            <p className="text-xs text-zinc-600 text-center mt-3">*Standard projects. Complex edits may vary.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FINAL CTA SECTION
// ============================================
function FinalCTASection() {
  return (
    <section className="py-20 bg-gradient-to-b from-zinc-900 to-zinc-950" data-testid="final-cta-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">
          Ready to be found?
        </h2>
        <p className="text-xl text-zinc-400 mb-8">
          Your next partner, investor, or client is searching for you right now.
          <span className="text-white"> Make sure they find you.</span>
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/profile/create" 
            className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-8 py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
          >
            Create Your Profile
            <ArrowRight className="w-5 h-5" />
          </Link>
          <a 
            href="#contact" 
            className="border border-zinc-700 hover:border-zinc-500 text-white px-8 py-4 rounded-lg font-bold transition-colors"
          >
            Book a Professional Interview
          </a>
        </div>
      </div>
    </section>
  );
}

// ============================================
// CONTACT SECTION
// ============================================
function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    marketing_consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Contact form error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-zinc-950 border-t border-zinc-800" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <p className="text-[#C41E3A] text-sm font-bold tracking-wider uppercase mb-3">Get In Touch</p>
            <h2 className="text-3xl font-black tracking-tight mb-6">
              Let's discuss your leadership story
            </h2>
            <p className="text-zinc-400 mb-8">
              Whether you want to create a profile, book a professional interview, or explore trade show coverage—we're here to help.
            </p>
            
            <div className="space-y-4">
              <a href="mailto:operations@brandstage-media.com" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors">
                <Mail className="w-5 h-5 text-[#C41E3A]" />
                operations@brandstage-media.com
              </a>
              <a href="tel:+919003035900" className="flex items-center gap-3 text-zinc-300 hover:text-white transition-colors">
                <Phone className="w-5 h-5 text-[#C41E3A]" />
                +91 9003035900
              </a>
              <div className="flex items-center gap-3 text-zinc-300">
                <Globe className="w-5 h-5 text-[#C41E3A]" />
                Bengaluru, India
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                <p className="text-zinc-400">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <textarea
                    placeholder="How can we help?"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-[#C41E3A] focus:outline-none transition-colors resize-none"
                    required
                  />
                </div>
                
                {/* Marketing Consent Checkbox - GDPR/DPDP Compliance */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="marketing_consent"
                    checked={formData.marketing_consent}
                    onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                    className="mt-1 w-4 h-4 bg-zinc-800 border-zinc-600 rounded text-[#C41E3A] focus:ring-[#C41E3A] focus:ring-offset-zinc-900"
                    data-testid="marketing-consent-checkbox"
                  />
                  <label htmlFor="marketing_consent" className="text-zinc-400 text-sm leading-relaxed">
                    I agree to receive marketing communications, updates, and promotional content from BrandStage. 
                    <span className="text-zinc-500 block mt-1">You can unsubscribe at any time. See our <a href="/privacy" className="text-[#C41E3A] hover:underline">Privacy Policy</a>.</span>
                  </label>
                </div>
                
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white py-3 rounded-lg font-bold transition-colors"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// FOOTER
// ============================================
function Footer() {
  return (
    <footer className="py-8 bg-zinc-950 border-t border-zinc-800">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <BrandLogo className="h-10" testId="landing-footer-logo" />
            </div>
            <span className="text-zinc-500 text-sm">Bengaluru, India</span>
          </div>
          
          <div className="flex items-center gap-6 text-sm text-zinc-500">
            <a href="tel:+919003035900" className="hover:text-white transition-colors">+91 9003035900</a>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          </div>
          
          <p className="text-zinc-500 text-sm">
            © {new Date().getFullYear()} BrandStage. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// MAIN LANDING PAGE
// ============================================
export default function LandingPage() {
  // Capture referral code from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      localStorage.setItem('brandstage_ref', refCode.toUpperCase());
      // Clean URL without reload
      const url = new URL(window.location);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.pathname + url.hash);
    }
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <HeroSection />
      <ProfileFeaturesSection />
      <VideoFirstSection />
      <OutcomesSection />
      <SocialProofSection />
      <LiveProfilesSection />
      <HowItWorksSection />
      <VideosSection />
      <TradeShowsSection />
      <ConnectSection />
      <PricingSection />
      <FinalCTASection />
      <ContactSection />
      <Footer />
    </div>
  );
}
